import { OneBlinkAutoSaveForm } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'

import config from '../../config'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { useAppConfig } from '../../providers/AppConfigProvider'
import useSubmitForm from '../../hooks/useSubmitForm'
import useCancelForm from '../../hooks/useCancelForm'
import useSaveDraft from '../../hooks/useSaveDraft'
import useAutoSaveKey from '../../hooks/useAutoSaveKey'
import useGoogleAnalyticsFormBeforeUnload from '../../hooks/useGoogleAnalyticsFormBeforeUnload'
import Dialog, {
  DialogHeadingText,
  DialogHeading,
  CloseIcon,
} from '../../components/Dialog'
import PaymentDialog from '../Payment/PaymentDialog'

type Props = {
  form: FormTypes.Form
  preFillData: Record<string, unknown>
}

function Form({ form, preFillData }: Props) {
  const { buttons, recaptchaKeyType, recaptchaPublicKey } = useAppConfig()
  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: form,
    preFillData,
    skipServerValidation: false,
  })

  const [onCancel, cancelError] = useCancelForm(form)
  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData,
  })

  const autoSaveKey = useAutoSaveKey(preFillData)
  const onBeforeUnload = useGoogleAnalyticsFormBeforeUnload({
    form,
    isSubmitting,
  })

  if (isSavingDraft) {
    return <LoadingSpinner>Saving Draft</LoadingSpinner>
  }

  if (cancelError) {
    return (
      <ErrorMessage>
        The form has been cancelled. Please close your browser window.
      </ErrorMessage>
    )
  }

  return (
    <>
      <OneBlinkAutoSaveForm
        audience="SUBMITTER"
        form={form}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onSaveDraft={onSaveDraft}
        onBeforeUnload={onBeforeUnload}
        googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
        captchaType={recaptchaKeyType}
        captchaSiteKey={recaptchaPublicKey}
        abnLookupAuthenticationGuid={config.ABN_AUTH_GUID}
        initialSubmission={preFillData}
        buttons={buttons}
        disabled={isSubmitting}
        autoSaveKey={autoSaveKey}
        removeAutoSaveDataBeforeSaveDraft={false}
        removeAutoSaveDataBeforeSubmit={false}
        isPendingQueueEnabled={false}
      />
      <Dialog isOpen={isSubmitting}>
        <LoadingSpinner>Submitting</LoadingSpinner>
      </Dialog>
      <Dialog isOpen={!!serverValidationMessage}>
        <DialogHeading>
          <DialogHeadingText>We could not submit your form</DialogHeadingText>
          <CloseIcon onClick={clearServerValidationMessage} />
        </DialogHeading>
        {serverValidationMessage}
      </Dialog>
      <PaymentDialog />
    </>
  )
}

export default Form
